import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, AppState } from '../../../types';
import { useCallback, useMemo, useRef, useState } from 'react';
import { FunnelMetricActions, FunnelMetricsDataType, FunnelsPayload } from '../../funnels';
import { isEmpty, some } from 'lodash';
import { AppCacheStatus } from '@fullstory/redux-util';
import { Timestamp } from '@fullstory/proto/google/protobuf';
import { FilteredElementRecord } from '@fullstory/renderer-shared';
import { AppCacheData } from '@fullstory/redux-util/utils/appCache/types';

export type HeatmapConversionFunnelPayloadWithStorageId = {
  storageId: string;
  ident: string;
  namedElement: FilteredElementRecord;
  payload: FunnelsPayload | null;
};

export const useHeatmapsConversionFunnelComputation = (
  payloadsToFetch: HeatmapConversionFunnelPayloadWithStorageId[],
) => {
  // HACK: conversion maps can issue lots of funnel requests at the same time
  //       those requests each transition through multiple states quickly which can cause a lot of unnecessary re-renders
  //       we override the selector equality function to avoid re-renders when the funnel metrics change
  //       but rather send those updates through a throttled state update
  const [_, setEnsureLatest] = useState(0);
  const funnelMetricsRef = useRef<{ [storageId: string]: AppCacheData<FunnelMetricsDataType> }>({});
  const funnelMetrics = funnelMetricsRef.current;
  const timeoutRef = useRef<number>();
  useSelector(
    (state: AppState) => state.funnels.funnelMetrics,
    fm => {
      if (fm !== funnelMetricsRef.current) {
        funnelMetricsRef.current = fm;
        window.clearTimeout(timeoutRef.current);
        timeoutRef.current = window.setTimeout(() => setEnsureLatest(cur => cur + 1), 500);
      }
      return true;
    },
  );

  const dispatch = useDispatch<AppDispatch>();
  const storageIds = payloadsToFetch.map(p => p.storageId);

  const forceCacheSwap = useCallback(() => {
    storageIds.forEach(storageId => {
      if (funnelMetrics[storageId]?.status === AppCacheStatus.HasData) {
        dispatch(FunnelMetricActions.appCacheSwap(storageId));
      }
    });
  }, [storageIds, dispatch, funnelMetrics]);

  const { funnelStats, computedAt, status, timestamp } = useMemo(() => {
    if (isEmpty(funnelMetrics) || isEmpty(payloadsToFetch)) {
      return {};
    }

    // Use the "lowest common denominator" status
    let status = AppCacheStatus.Initial;
    if (
      some(payloadsToFetch, p => funnelMetrics[p.storageId]?.status === AppCacheStatus.IsLoading)
    ) {
      status = AppCacheStatus.IsLoading;
    } else if (
      some(payloadsToFetch, p => funnelMetrics[p.storageId]?.status === AppCacheStatus.IsRefetching)
    ) {
      status = AppCacheStatus.IsRefetching;
    } else if (
      some(payloadsToFetch, p => funnelMetrics[p.storageId]?.status === AppCacheStatus.LoadFailed)
    ) {
      status = AppCacheStatus.LoadFailed;
    } else if (
      some(payloadsToFetch, p => funnelMetrics[p.storageId]?.status === AppCacheStatus.Fresh)
    ) {
      status = AppCacheStatus.Fresh;
    } else {
      status = AppCacheStatus.HasData;
    }

    const response = {
      data: payloadsToFetch.map(p => ({
        key: p.storageId,
        ident: p.ident,
        namedElement: p.namedElement,
        data: funnelMetrics[p.storageId]?.data,
      })),
      status,
    };

    return {
      funnelStats: response.data,
      computedAt: response.data?.[0]?.data?.funnelMetrics.computedAt,
      status: response.status,
      timestamp: Timestamp.toDate(response.data?.[0]?.data?.funnelMetrics.computedAt),
    };
  }, [payloadsToFetch, funnelMetrics]);

  return {
    funnelStats,
    computedAt,
    forceCacheSwap,
    status: status ?? AppCacheStatus.Initial,
    timestamp,
  };
};
